"use client";

import { useState } from "react";
import Link from "next/link";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatUSDBalance } from "@/hooks/useSmartAccountBalances";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ActiveStrategyItem {
  id: string;
  name: string;
  badge: string;
  description: string;
  tokens: { symbol: string }[];
  apy: string;
  balanceUsd: number;
}

// ---------------------------------------------------------------------------
// TokenAvatar (small, for overlapping pair)
// ---------------------------------------------------------------------------

function TokenAvatar({
  symbol,
  logoUrl,
  size = 28,
}: {
  symbol: string;
  logoUrl?: string | null;
  size?: number;
}) {
  const [imgError, setImgError] = useState(false);
  const showFallback = !logoUrl || imgError;
  const resolvedSrc = logoUrl ?? "";
  const sz = `${String(size)}px`;

  return (
    <div
      className="rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden"
      style={{
        width: sz,
        height: sz,
        background: showFallback ? "var(--accent-dim)" : "var(--surface-hi)",
        border: "1px solid var(--border-subtle)",
        fontSize: 9,
      }}
      aria-hidden="true"
    >
      {showFallback ? (
        <span className="font-semibold uppercase" style={{ color: "var(--accent)" }}>
          {symbol.slice(0, 2)}
        </span>
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={resolvedSrc}
          alt={symbol}
          width={size - 6}
          height={size - 6}
          onError={() => { setImgError(true); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// StrategyCard
// ---------------------------------------------------------------------------

interface StrategyCardProps {
  strategy: ActiveStrategyItem;
  getLogoBySymbol: (s: string) => string | null;
  animDelay?: string;
}

export function StrategyCard({ strategy, getLogoBySymbol, animDelay }: StrategyCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-xl p-4 flex flex-col gap-3",
        animDelay && "anim-fadeSlideInUp-500ms",
      )}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border-subtle)",
        ...(animDelay ? { animationDelay: animDelay } : {}),
      }}
    >
      {/* Top row: badge + token pair avatars */}
      <div className="flex items-start justify-between">
        <span
          className="text-[10px] font-medium tracking-[0.06em] uppercase px-2 py-0.5 rounded"
          style={{
            background: "var(--surface-hi)",
            border: "1px solid var(--border-subtle)",
            color: "var(--text-secondary)",
          }}
        >
          {strategy.badge}
        </span>

        {/* Overlapping token pair avatars */}
        <div className="flex items-center" style={{ marginRight: "-2px" }}>
          {strategy.tokens.slice(0, 2).map((t, i) => (
            <div
              key={t.symbol}
              style={{ marginLeft: i === 0 ? 0 : "-8px", zIndex: 2 - i, position: "relative" }}
            >
              <TokenAvatar symbol={t.symbol} logoUrl={getLogoBySymbol(t.symbol)} size={28} />
            </div>
          ))}
        </div>
      </div>

      {/* APY hero */}
      <p
        className="font-serif font-normal leading-none"
        style={{ fontSize: "1.6rem", color: "var(--green)" }}
      >
        {strategy.apy}
      </p>

      {/* Name + description */}
      <div>
        <p
          className="text-[13px] font-medium leading-tight"
          style={{ color: "var(--text-primary)" }}
        >
          {strategy.name}
        </p>
        <p
          className="text-[11px] mt-0.5 leading-snug truncate"
          style={{ color: "var(--text-secondary)" }}
        >
          {strategy.description}
        </p>
      </div>

      {/* Footer: USD balance + external link */}
      <div className="flex items-center justify-between mt-auto pt-1">
        <p
          className="text-[12px] font-mono"
          style={{ color: "var(--text-primary)" }}
        >
          {formatUSDBalance(strategy.balanceUsd)}
        </p>
        <Link
          href="/strategies"
          aria-label={`View ${strategy.name} strategy details`}
          className="opacity-40 hover:opacity-80 transition-opacity rounded focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]"
        >
          <ExternalLink size={13} style={{ color: "var(--text-secondary)" }} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}
